/**
 * 제휴사 상세 페이지
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Badge, Button, Spinner, Row, Col } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import ApiKeyManager from '../../components/partners/ApiKeyManager';
import { partnerService } from '../../services/partnerService';
import { Partner } from '../../types/partner';
import { format } from 'date-fns';

const PartnerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const partnerId = id ? parseInt(id, 10) : 0;

  const { data: partner, isLoading } = useQuery({
    queryKey: ['partner', partnerId],
    queryFn: () => partnerService.getPartner(partnerId),
    enabled: !!partnerId && partnerId > 0,
  });

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge bg="success">활성</Badge>
    ) : (
      <Badge bg="secondary">비활성</Badge>
    );
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!partner) {
    return (
      <AdminLayout>
        <Card>
          <Card.Body>
            <p>제휴사를 찾을 수 없습니다.</p>
            <Button variant="secondary" onClick={() => navigate('/admin/partners')}>
              목록으로
            </Button>
          </Card.Body>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>제휴사 상세</h1>
          <div>
            <Button
              variant="secondary"
              className="me-2"
              onClick={() => navigate('/admin/partners')}
            >
              목록으로
            </Button>
            <Button
              variant="primary"
              onClick={() => navigate(`/admin/partners/${partner.id}/edit`)}
            >
              수정
            </Button>
          </div>
        </div>

        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">기본 정보</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <label className="text-muted small">제휴사명</label>
                  <div className="fw-bold">{partner.partner_name}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="text-muted small">제휴사 코드</label>
                  <div className="fw-bold">{partner.partner_code}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="text-muted small">상태</label>
                  <div>{getStatusBadge(partner.status)}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="text-muted small">수수료율</label>
                  <div className="fw-bold" style={{ color: '#00BCD4' }}>
                    {partner.commission_rate}%
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="text-muted small">담당자명</label>
                  <div>{partner.contact_name || '-'}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="text-muted small">이메일</label>
                  <div>{partner.email || '-'}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="text-muted small">연락처</label>
                  <div>{partner.phone || '-'}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="text-muted small">등록일시</label>
                  <div>
                    {partner.created_at
                      ? format(new Date(partner.created_at), 'yyyy-MM-dd HH:mm:ss')
                      : '-'}
                  </div>
                </div>
              </Col>
              {partner.updated_at && (
                <Col md={6}>
                  <div className="mb-3">
                    <label className="text-muted small">수정일시</label>
                    <div>
                      {format(new Date(partner.updated_at), 'yyyy-MM-dd HH:mm:ss')}
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </Card.Body>
        </Card>

        <ApiKeyManager
          partnerId={partner.id}
          partner={partner}
        />
      </div>
    </AdminLayout>
  );
};

export default PartnerDetail;

